import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, Bot, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface SupportChatScreenProps {
  onBack: () => void;
}
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}
export function SupportChatScreen({
  onBack
}: SupportChatScreenProps) {
  const {
    isLight
  } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: "Hi there! I'm your Vexu.ai support assistant. How can I help you today?",
    sender: 'ai',
    timestamp: new Date()
  }]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = () => {
    if (!inputValue.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setIsTyping(true);
    // Mock AI response
    setTimeout(() => {
      const aiResponses = ['I can certainly help with that. Let me check your account settings.', "That's a great question. You can configure that in the Caller Experience section.", "I've noted that down. Is there anything else you need assistance with?", 'To update your forwarding rules, please visit the Home screen and tap the settings icon on the forwarding card.'];
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponses[Math.floor(Math.random() * aiResponses.length)],
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, newAiMsg]);
      setIsTyping(false);
    }, 1500 + Math.random() * 1000);
  };
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="h-full flex flex-col relative" data-id="element-3847" data-mp="1ikxru20gd75o">
      {/* Header */}
      <div className={`px-6 pt-6 pb-4 flex items-center gap-3 z-20 ${isLight ? 'bg-white/80 backdrop-blur-xl border-b border-slate-200/50' : 'bg-dark/80 backdrop-blur-xl border-b border-white/5'}`} data-id="element-3848" data-mp="1mlrk17kxh84d">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-3849" data-mp="1wb1akj04iiqd">
          <ArrowLeft className="w-5 h-5" data-id="element-3850" data-mp="2aaarxm02fivx" />
        </button>
        <div className="flex items-center gap-3" data-id="element-3851" data-mp="2a8psbhyw99r7">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-600' : 'bg-vexu/20 text-vexu'}`} data-id="element-3852" data-mp="3uu65933yqpuy">
            <Bot className="w-5 h-5" data-id="element-3853" data-mp="3lrwtyb0vesgi" />
          </div>
          <div data-id="element-3854" data-mp="3luizk547axw3">
            <h1 className={`text-base font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3855" data-mp="3lu9hd3pedv0y">
              Support Agent
            </h1>
            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-3856" data-mp="idsovcxobwo2">
              <div className="w-1.5 h-1.5 rounded-full bg-vexu animate-pulse" data-id="element-3857" data-mp="ddo7lkoxyxws" />
              <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3858" data-mp="8ld4k8ywmw00">
                Online • Replies instantly
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-32" data-id="element-3859" data-mp="wbr1odwcjl1g">
        <AnimatePresence initial={false} data-id="element-3860" data-mp="w9eada4p7ybq">
          {messages.map(msg => {
          const isAi = msg.sender === 'ai';
          return <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} className={`flex gap-3 max-w-[85%] ${isAi ? 'self-start' : 'self-end ml-auto flex-row-reverse'}`} data-id="element-3861" data-mp="kvksdoc4k2jc">
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-auto ${isAi ? isLight ? 'bg-vexu/10 text-vexu-600' : 'bg-vexu/20 text-vexu' : isLight ? 'bg-slate-200 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3862" data-mp="pu5qni7tgc55">
                  {isAi ? <Bot className="w-4 h-4" data-id="element-3863" data-mp="1n9td4v8pw5r0" /> : <User className="w-4 h-4" data-id="element-3864" data-mp="37zyrjb4yaemz" />}
                </div>

                {/* Bubble */}
                <div className={`flex flex-col ${isAi ? 'items-start' : 'items-end'}`} data-id="element-3865" data-mp="331dtnid6871p">
                  <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${isAi ? isLight ? 'bg-white border border-slate-200/60 text-slate-800 shadow-sm rounded-bl-sm' : 'bg-[#1a1a1a] border border-white/5 text-zinc-200 shadow-lg rounded-bl-sm' : 'bg-vexu text-white shadow-md rounded-br-sm'}`} data-id="element-3866" data-mp="3gqz2hzx9b5w0">
                    {msg.text}
                  </div>
                  <span className={`text-[10px] mt-1.5 px-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3867" data-mp="2y2kdfi6i21xu">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="flex gap-3 max-w-[85%]" data-id="element-3868" data-mp="32589rao5hacc">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-auto ${isLight ? 'bg-vexu/10 text-vexu-600' : 'bg-vexu/20 text-vexu'}`} data-id="element-3869" data-mp="3g3f0m6uoma2i">
              <Bot className="w-4 h-4" data-id="element-3870" data-mp="2nfsmi4ntvn1e" />
            </div>
            <div className={`px-4 py-3.5 rounded-2xl rounded-bl-sm flex items-center gap-1.5 ${isLight ? 'bg-white border border-slate-200/60 shadow-sm' : 'bg-[#1a1a1a] border border-white/5 shadow-lg'}`} data-id="element-3871" data-mp="2sedmq432y6pu">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0
          }} className="w-1.5 h-1.5 rounded-full bg-vexu/60" data-id="element-3872" data-mp="1zpf12wzr0mfs" />
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0.2
          }} className="w-1.5 h-1.5 rounded-full bg-vexu/60" data-id="element-3873" data-mp="1s4m9h7m102mb" />
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            repeat: Infinity,
            duration: 0.6,
            delay: 0.4
          }} className="w-1.5 h-1.5 rounded-full bg-vexu/60" data-id="element-3874" data-mp="19fo2va9j6can" />
            </div>
          </motion.div>}
        <div ref={messagesEndRef} data-id="element-3875" data-mp="qhbuvrucv8gn" />
      </div>

      {/* Input Area */}
      <div className={`absolute bottom-0 left-0 right-0 p-4 pt-2 z-20 ${isLight ? 'bg-gradient-to-t from-[#f8faf9] via-[#f8faf9] to-transparent' : 'bg-gradient-to-t from-dark via-dark to-transparent'}`} data-id="element-3876" data-mp="qj6coxabykgi">
        <div className={`flex items-end gap-2 p-2 rounded-3xl backdrop-blur-xl border shadow-lg ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/80 border-white/10'}`} data-id="element-3877" data-mp="25d3ax42krifz">
          <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }} placeholder="Type your message..." className={`flex-1 max-h-32 min-h-[44px] bg-transparent border-none resize-none px-3 py-3 text-sm outline-none focus:outline-none focus:ring-0 focus:border-none ${isLight ? 'text-slate-900 placeholder:text-slate-400' : 'text-white placeholder:text-zinc-500'}`} rows={1} data-id="element-3878" data-mp="1vnjus668y2ot" />
          <button onClick={handleSend} disabled={!inputValue.trim()} className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all ${inputValue.trim() ? 'bg-vexu text-white shadow-md hover:bg-vexu-600' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-600'}`} data-id="element-3879" data-mp="y2icrrz713a6">
            <Send className="w-5 h-5 ml-0.5" data-id="element-3880" data-mp="1h0ba8er1sl06" />
          </button>
        </div>
      </div>
    </div>;
}